import {
  REQUIREMENT_STATUS_FIELD,
  REQUIREMENT_TO_FACTOR_KEY
} from './pricingService';
import { flattenActivityLines } from '../utils/factorInstances';

// Legacy WhatsApp templates (still used by Case Study WhatsApp button)
export const whatsappTemplates = {
  'Not Started': {
    subject: 'PF Claim Initiated',
    message: `Hi {{memberName}},

Your PF claim has been successfully initiated with the following details:

📋 Requirements:
{{requirementsWithStatus}}

💰 PF Balance: ₹{{pfBalance}}
💰 EPS Balance: ₹{{epsBalance}}
💵 Total Balance: ₹{{totalBalance}}
💳 Fees: ₹{{fees}}
💳 Advance: ₹{{advance}}

📊 Current Status: {{status}}

We will keep you updated on the progress. Please ensure all required documents are ready.

For any queries, please contact us.

Best regards,
PF Claims Management Team`
  },

  'Work in Progress': {
    subject: 'PF Claim Progress Update',
    message: `Hi {{memberName}},

Good news! Your PF claim is progressing well:

📋 Requirements:
{{requirementsWithStatus}}

💵 Total Balance: ₹{{totalBalance}}
💳 Fees: ₹{{fees}}
💳 Advance: ₹{{advance}}

🔍 Activity Status:
{{activityStatus}}

📊 Current Status: {{status}}

We are working on your case and will complete it soon. Please stay patient.

For any urgent queries, please contact us immediately.

Best regards,
PF Claims Management Team`
  },

  'Pending with Employer/PF Office': {
    subject: 'PF Claim Pending - Action Required',
    message: `Hi {{memberName}},

Your PF claim is currently pending with Employer/PF Office due to the following:

📋 Requirements:
{{requirementsWithStatus}}

💵 Total Balance: ₹{{totalBalance}}
💳 Fees: ₹{{fees}}
💳 Advance: ₹{{advance}}

⚠️ Pending Items:
{{pendingItems}}

📊 Current Status: {{status}}

Please provide the required documents or complete the necessary formalities to proceed further.

For assistance, please contact us.

Best regards,
PF Claims Management Team`
  },

  'Completed': {
    subject: 'PF Claim Successfully Completed',
    message: `🎉 Congratulations {{memberName}}!

Your PF claim has been successfully completed:

📋 Requirements:
{{requirementsWithStatus}}

💵 Total Balance: ₹{{totalBalance}}
💳 Fees Collected: ₹{{fees}}
💳 Advance Given: ₹{{advance}}
💵 Net Amount: ₹{{netAmount}}

✅ All activities completed successfully!

Your claim process is now complete. You should receive the settlement amount as per the PF rules.

Thank you for choosing our services!

Best regards,
PF Claims Management Team`
  }
};

const calculateOverallStatus = (claim) => {
  const selectedRequirements = claim.requirements || [];

  if (selectedRequirements.length === 0) {
    return 'Not Started';
  }

  const requirementStatusMap = {
    'PF Activation': claim.pf_activation_status || 'Not Started',
    'Joint Declaration Filing': claim.joint_declaration_filing_status || 'Not Started',
    'KYC Updation': claim.kyc_status || 'Not Started',
    'E-Nomination Filing': claim.e_nomination_status || 'Not Started',
    'Contribution Adjustment': claim.contribution_adjustment_status || 'Not Started',
    'Exit Date Correction': claim.exit_date_status || 'Not Started',
    'Transfers': claim.transfers_status || 'Not Started',
    'PF Advance Claim': claim.advance_claim_status || claim.pf_advance_claim_status || 'Not Started',
    'PF Final Settlement': claim.pf_final_settlement_status || 'Not Started',
    'Scheme Certificate Application': claim.scheme_certificate_status || 'Not Started',
    'Monthly Pension application': claim.monthly_pension_application_status || 'Not Started',
    'Manual Pension Apply': claim.manual_pension_apply_status || 'Not Started',
    'Death Claim - PF, EDLI & EPS application': claim.death_claim_pf_edli_eps_application_status || 'Not Started',
    'Death Claim Handling': claim.death_claim_pf_edli_eps_application_status || 'Not Started',
    'EDLI Claim': claim.death_claim_pf_edli_eps_application_status || 'Not Started',
    'EPS Merging Issues': claim.eps_merging_status || 'Not Started'
  };

  const selectedActivityStatuses = selectedRequirements.map(
    (req) => requirementStatusMap[req] || 'Not Started'
  );

  if (selectedActivityStatuses.every((s) => s === 'Not Started')) return 'Not Started';
  if (selectedActivityStatuses.every((s) => s === 'Completed')) return 'Completed';
  return 'Work in Progress';
};

const generateRequirementsWithStatus = (claim) => {
  const requirements = [];

  if (claim.requirements && Array.isArray(claim.requirements)) {
    claim.requirements.forEach((requirement) => {
      const field = REQUIREMENT_STATUS_FIELD[requirement];
      const status = (field && claim[field]) || 'Not Started';
      requirements.push(`• ${requirement} - *${status}*`);
    });
  } else {
    const requirementList = claim.requirement ? claim.requirement.split(', ') : [];
    requirementList.forEach((req) => {
      requirements.push(`• ${req} - *Not Started*`);
    });
  }

  return requirements.join('\n');
};

/**
 * Option C — chat status update:
 * Updated (changed only) + Unchanged, no fees/balance.
 * Current status bolded with *...* (WhatsApp-style).
 */
export const generateClaimStatusChatMessage = ({
  member,
  claimId,
  previous,
  next,
  requirements = [],
  finalStatus
}) => {
  const prevLines = flattenActivityLines({
    requirements,
    statuses: previous?.statuses || {},
    requirementStatusField: REQUIREMENT_STATUS_FIELD,
    factorInstances: previous?.factorInstances || {},
    factorKeyByRequirement: REQUIREMENT_TO_FACTOR_KEY
  });
  const nextLines = flattenActivityLines({
    requirements,
    statuses: next?.statuses || {},
    requirementStatusField: REQUIREMENT_STATUS_FIELD,
    factorInstances: next?.factorInstances || {},
    factorKeyByRequirement: REQUIREMENT_TO_FACTOR_KEY
  });

  const prevById = Object.fromEntries(prevLines.map((l) => [l.id, l]));
  const updated = [];

  nextLines.forEach((line) => {
    const prevStatus = prevById[line.id]?.status || 'Not Started';
    const currStatus = line.status || 'Not Started';
    if (prevStatus !== currStatus) {
      updated.push(`• ${line.label}: ${prevStatus} → *${currStatus}*`);
    }
  });

  const name = member?.name || 'Valued Member';
  const claimRef = claimId ? `Claim #${claimId}` : 'your claim';
  const parts = [
    `Hi ${name},`,
    '',
    `${claimRef} — activity status update:`
  ];

  if (updated.length) {
    parts.push('', 'Updated:', ...updated);
  } else {
    parts.push('', 'Updated:', '• No activity status changes in this save.');
  }

  parts.push(
    '',
    `Overall: *${finalStatus || 'Work in Progress'}*`,
    '',
    'Please stay patient. Contact us for any urgent queries.',
    '',
    '— PF Claims Management Team'
  );

  return {
    subject: 'PF Claim Progress Update',
    message: parts.join('\n')
  };
};

export const generateWhatsAppMessage = (claim, member, status) => {
  const finalStatus = status || calculateOverallStatus(claim);
  const template = whatsappTemplates[finalStatus] || whatsappTemplates['Not Started'];
  let message = template.message;

  message = message.replace(/\{\{memberName\}\}/g, member.name || 'Valued Member');
  message = message.replace(/\{\{requirements\}\}/g, claim.requirement || 'PF Services');
  message = message.replace(/\{\{requirementsWithStatus\}\}/g, generateRequirementsWithStatus(claim));
  message = message.replace(/\{\{pfBalance\}\}/g, parseFloat(claim.pf_balance || 0).toLocaleString());
  message = message.replace(/\{\{epsBalance\}\}/g, parseFloat(claim.eps_balance || 0).toLocaleString());
  message = message.replace(/\{\{totalBalance\}\}/g, parseFloat(claim.total_balance || 0).toLocaleString());
  message = message.replace(/\{\{fees\}\}/g, parseFloat(claim.fees || 0).toLocaleString());
  message = message.replace(/\{\{advance\}\}/g, parseFloat(claim.advance || 0).toLocaleString());
  message = message.replace(/\{\{netAmount\}\}/g, (parseFloat(claim.fees || 0) - parseFloat(claim.advance || 0)).toLocaleString());
  message = message.replace(/\{\{status\}\}/g, finalStatus);
  message = message.replace(/\{\{pendingItems\}\}/g, 'Please check pending activities with our team.');

  if (finalStatus === 'Work in Progress') {
    const activities = [];
    const pushActivity = (label, value) => {
      if (!value || value === 'Not Started') return;
      const icon = value === 'Completed' ? '✅'
        : value === 'Pending with Employer/PF Office' ? '⚠️' : '🔄';
      activities.push(`${icon} ${label}: ${value}`);
    };
    pushActivity('PF Activation', claim.pf_activation_status);
    pushActivity('KYC', claim.kyc_status);
    pushActivity('E-Nomination', claim.e_nomination_status);
    pushActivity('Exit Date', claim.exit_date_status);
    pushActivity('Transfers', claim.transfers_status);
    pushActivity('Advance Claim', claim.advance_claim_status || claim.pf_advance_claim_status);
    pushActivity('PF Final Settlement', claim.pf_final_settlement_status);
    pushActivity('Scheme Certificate', claim.scheme_certificate_status);
    pushActivity('EPS Merging', claim.eps_merging_status);
    message = message.replace(
      /\{\{activityStatus\}\}/g,
      activities.length > 0 ? activities.join('\n') : 'Processing in progress...'
    );
  }

  return {
    subject: template.subject,
    message
  };
};

export const sendWhatsAppMessage = (phoneNumber, claim, member, status) => {
  if (!phoneNumber || !member.whatsapp) {
    return null;
  }

  const { subject, message } = generateWhatsAppMessage(claim, member, status);
  const encodedMessage = encodeURIComponent(`${subject}\n\n${message}`);

  let cleanPhone = phoneNumber.replace(/\D/g, '');
  if (cleanPhone.startsWith('91')) {
    cleanPhone = cleanPhone.substring(2);
  } else if (cleanPhone.startsWith('0')) {
    cleanPhone = cleanPhone.substring(1);
  }

  return `https://wa.me/91${cleanPhone}?text=${encodedMessage}`;
};
